import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BoardApi } from "../../../services/board-service-api";
import { withManagement } from "../../../shared/hoc/with-management";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import { copyTextToClipboard } from "../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import { KUIKA_COPY_RESET_MS } from "../../ui-kit/kuika-copy-button/kuika-copy-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import "./board.scss";
import "./issue-full-page.scss";
import { buildBoardPath } from "./board-routing";
import { BoardService } from "./board-service";
import { issueHeaderChanged } from "./change-event-routing";
import { confirmDialog, ConfirmDialogHost } from "./confirm-service";
import IssueDetail from "./issue-detail";
import { Board, BoardChangeEvent, BoardStatus, Issue, Project, UpdateIssueInput, boardProjectKeys } from "./types";
import { useBoardConfig } from "./use-board";

const IssueFullPageInner: React.FC = () => {
  const { config } = useBoardConfig();
  const navigate = useNavigate();
  const { domainName, issueKey } = useParams<{ domainName: string; issueKey: string }>();

  // Resolve the single issue by its URL key on demand (no full-snapshot scan); re-resolve only
  // when a mutation changes *this* issue's header so the status/summary stay current. The
  // embedded IssueDetail scopes its own bundle refetch.
  const [issue, setIssue] = useState<Issue | null>(null);
  const [resolved, setResolved] = useState<boolean>(false);
  const issueIdRef = useRef<string | null>(null);
  issueIdRef.current = issue?.id ?? null;
  useEffect(() => {
    if (!issueKey) {
      setIssue(null);
      setResolved(true);
      return;
    }
    let cancelled: boolean = false;
    const load = async (): Promise<void> => {
      try {
        const found: Issue = await BoardApi.getIssueByKey(issueKey);
        if (!cancelled) setIssue(found);
      } catch {
        if (!cancelled) setIssue(null);
      } finally {
        if (!cancelled) setResolved(true);
      }
    };
    void load();
    const unsubscribe: () => void = BoardService.subscribe((event: BoardChangeEvent) => {
      const id: string | null = issueIdRef.current;
      if (id !== null && issueHeaderChanged(event, id)) void load();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [issueKey]);

  const project: Project | undefined = useMemo(
    () => (issue ? config.projects.find((entry: Project) => entry.key === issue.projectKey) : undefined),
    [config.projects, issue]
  );

  // Find a sensible board to return to: the project board for this project, else the first board.
  const homeBoard: Board | null = useMemo(() => {
    if (!issue) return config.boards[0] ?? null;
    const projectBoard: Board | undefined = config.boards.find(
      (board: Board) => board.scope.kind === "project" && board.scope.projectKey === issue.projectKey
    );
    if (projectBoard) return projectBoard;
    const multiBoard: Board | undefined = config.boards.find(
      (board: Board) => board.scope.kind === "multi_project" && boardProjectKeys(board).includes(issue.projectKey)
    );
    return multiBoard ?? config.boards[0] ?? null;
  }, [config.boards, issue]);

  // Unknown issue (once resolution finished) → bounce back to board index.
  useEffect(() => {
    if (resolved && issueKey && !issue) {
      navigate(`/${domainName}/board`, { replace: true });
    }
  }, [resolved, issueKey, issue, domainName, navigate]);

  // Status editing lives in the detail rail now; the page only needs the project's statuses to pass
  // down (status is project-owned).
  const projectStatuses: BoardStatus[] = useMemo(
    () => (issue ? config.statuses.filter((entry: BoardStatus) => entry.projectKey === issue.projectKey) : []),
    [issue, config.statuses]
  );

  // Brief "Copied!" confirmation on the copy-link button (resets after a moment).
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const copyTimerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    };
  }, []);

  if (!issue) return null;

  const goBack = (): void => {
    if (homeBoard) {
      navigate(`${buildBoardPath(domainName ?? "", homeBoard)}/${issue.key}`);
    } else {
      navigate(`/${domainName}/board`);
    }
  };

  const copyLink = (): void => {
    const url: string = `${window.location.origin}/${domainName}/issue/${issue.key}`;
    // The shared helper falls back to the textarea copy when the Clipboard API is missing or refused,
    // so the check only goes up for a copy that really happened.
    void copyTextToClipboard(url).then((succeeded: boolean): void => {
      if (!succeeded) return;
      setLinkCopied(true);
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setLinkCopied(false), KUIKA_COPY_RESET_MS);
    });
  };

  return (
    <div className="kuika-board kuika-board--page" data-testid="kuika-board-full">
      <div className="kuika-board__page-header">
        <div className="kuika-board__page-breadcrumb">
          <KuikaTooltip content="Back to board" position="bottom">
            <KuikaButton type="tertiary" color="neutral" size="sm" isIcon onClick={goBack} data-testid="issue-back">
              <KuikaSvg type="arrows" name="arrow-left" width={16} height={16} />
            </KuikaButton>
          </KuikaTooltip>
          <span className="kuika-board__page-crumb">
            {project && (
              <span className="kuika-board__subtitle-dot" style={{ background: project.color }} aria-hidden="true" />
            )}
            <span
              className="kuika-board__page-crumb-link"
              onClick={() => homeBoard && navigate(buildBoardPath(domainName ?? "", homeBoard))}
              role="link"
              tabIndex={0}
            >
              {homeBoard?.name ?? "Board"}
            </span>
            <span className="kuika-board__page-crumb-sep">/</span>
            <span className="kuika-board__panel-key">{issue.key}</span>
          </span>
        </div>
        <div className="kuika-board__panel-actions">
          <KuikaTooltip content={linkCopied ? "Copied!" : "Copy link"} position="bottom">
            <KuikaButton
              type="tertiary"
              color="neutral"
              size="sm"
              isIcon
              onClick={copyLink}
              data-testid="issue-copy-link"
              aria-label="Copy link to issue"
              className={linkCopied ? "kuika-board__copy-active" : ""}
            >
              <KuikaSvg name={linkCopied ? "check" : "link"} width={16} height={16} />
            </KuikaButton>
          </KuikaTooltip>
          <KuikaTooltip content="Collapse to drawer" position="bottom">
            <KuikaButton type="tertiary" color="neutral" size="sm" isIcon onClick={goBack} data-testid="issue-collapse">
              <KuikaSvg name="maximize-off" width={16} height={16} />
            </KuikaButton>
          </KuikaTooltip>
          <KuikaTooltip content="Delete issue" position="bottom">
            <KuikaButton
              type="tertiary"
              color="danger"
              size="sm"
              isIcon
              onClick={() => {
                void confirmDialog({
                  title: "Delete issue",
                  message: `Delete ${issue.key}? This can't be undone.`,
                  confirmLabel: "Delete",
                  danger: true
                }).then((ok: boolean) => {
                  if (!ok) return;
                  BoardService.deleteIssue(issue.id);
                  if (homeBoard) navigate(buildBoardPath(domainName ?? "", homeBoard));
                  else navigate(`/${domainName}/board`);
                });
              }}
              data-testid="issue-delete"
            >
              <KuikaSvg name="trash" width={16} height={16} />
            </KuikaButton>
          </KuikaTooltip>
        </div>
      </div>
      <div className="kuika-board__page-scroll">
        <div className="kuika-board__page-shell">
          <IssueDetail
            issue={issue}
            statuses={projectStatuses}
            project={project}
            layout="page"
            onPatchIssue={(patch: UpdateIssueInput) =>
              setIssue((prev: Issue | null) => (prev ? { ...prev, ...patch } : prev))
            }
          />
        </div>
      </div>
      <ConfirmDialogHost />
    </div>
  );
};

const IssueFullPage = withManagement<{}>(IssueFullPageInner);
// The unwrapped page (no management shell) is reused by the in-designer board overlay.
export { IssueFullPage, IssueFullPageInner };
export default IssueFullPage;
